import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Button, useTheme, useMediaQuery } from '@mui/material';
import {
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  InfoOutlined as InfoIcon,
  WarningAmberOutlined as WarningIcon,
  Close as CloseIcon
} from '@mui/icons-material';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastData {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  }>;
}

interface ToastProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => setIsVisible(true));

    // Auto dismiss
    const duration = toast.duration ?? 4000;
    const timer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.duration]);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => onDismiss(toast.id), 200);
  };

  const getIcon = () => {
    const iconProps = { sx: { fontSize: 18 } };
    switch (toast.type) {
      case 'success':
        return <SuccessIcon {...iconProps} />;
      case 'error':
        return <ErrorIcon {...iconProps} />;
      case 'warning':
        return <WarningIcon {...iconProps} />;
      case 'info':
      default:
        return <InfoIcon {...iconProps} />;
    }
  };

  const getColors = () => {
    const isDark = theme.palette.mode === 'dark';
    switch (toast.type) {
      case 'success':
        return {
          bg: isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
          border: isDark ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)',
          icon: '#22c55e'
        };
      case 'error':
        return {
          bg: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
          border: isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)',
          icon: '#ef4444'
        };
      case 'warning':
        return {
          bg: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
          border: isDark ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.2)',
          icon: '#f59e0b'
        };
      case 'info':
      default:
        return {
          bg: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
          border: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)',
          icon: '#3b82f6'
        };
    }
  };

  const colors = getColors();

  const hasMultipleActions = toast.actions && toast.actions.length > 0;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: hasMultipleActions ? 'column' : 'row',
        gap: hasMultipleActions ? 1 : 1.5,
        py: 1.25,
        px: 2,
        borderRadius: '10px',
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(30, 30, 30, 0.95)' 
          : 'rgba(255, 255, 255, 0.98)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0, 0, 0, 0.4)'
          : '0 4px 20px rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(8px)',
        minWidth: 280,
        maxWidth: hasMultipleActions ? 420 : 400,
        transform: isVisible && !isLeaving ? 'translateY(0)' : 'translateY(16px)',
        opacity: isVisible && !isLeaving ? 1 : 0,
        transition: 'transform 0.2s ease, opacity 0.2s ease',
        pointerEvents: 'auto'
      }}
    >
      {/* Top row: Icon, Message, Close */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
        {/* Icon */}
        <Box sx={{ color: colors.icon, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {getIcon()}
        </Box>

        {/* Message */}
        <Typography
          sx={{
            flex: 1,
            fontSize: '13px',
            fontWeight: 500,
            color: theme.palette.text.primary,
            lineHeight: 1.4
          }}
        >
          {toast.message}
        </Typography>

        {/* Single action button (legacy support) */}
        {toast.action && !hasMultipleActions && (
          <Button
            size="small"
            onClick={() => {
              toast.action?.onClick();
              handleDismiss();
            }}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              color: colors.icon,
              minWidth: 'auto',
              px: 1,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: colors.bg
              }
            }}
          >
            {toast.action.label}
          </Button>
        )}

        {/* Close button */}
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{
            p: 0.5,
            color: theme.palette.text.secondary,
            flexShrink: 0,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.05)' 
                : 'rgba(0,0,0,0.04)'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Multiple action buttons */}
      {hasMultipleActions && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            width: '100%',
            justifyContent: 'flex-end',
            pl: 4 // Align with message text (icon width + gap)
          }}
        >
          {toast.actions!.map((action, index) => (
            <Button
              key={index}
              size="small"
              variant={action.variant === 'primary' ? 'contained' : 'text'}
              onClick={() => {
                action.onClick();
                handleDismiss();
              }}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: 600,
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                borderRadius: '6px',
                ...(action.variant === 'primary' ? {
                  backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
                  }
                } : {
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.04)'
                  }
                })
              }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Toast;

